import React, { forwardRef } from 'react';

import type { PaperProps, Theme } from '@mui/material';
import { Box, IconButton, Paper } from '@mui/material';
import type { CustomContentProps, VariantType } from 'notistack';
import { useSnackbar } from 'notistack';
import { CloseIcon } from 'src/components/icons/CloseIcon';
import { InfoIcon } from 'src/components/icons/InfoIcon';
import { SuccessIcon } from 'src/components/icons/SuccessIcon';
import { getTypographyForSx } from 'src/theme';

// Style functions
function getVariantStyles(variant: VariantType, theme: Theme): React.CSSProperties {
  switch (variant) {
    case 'success':
      return {
        borderLeft: `3px solid ${theme.appColors.accent2}`,
      };
    case 'info':
      return {
        borderLeft: `3px solid ${theme.appColors.accent1}`,
      };
    case 'error':
      return {
        borderLeft: `3px solid ${theme.appColors.accent6}`,
      };
    case 'warning':
      return {
        borderLeft: `3px solid ${theme.appColors.accent4}`,
      };
    default:
      return {};
  }
}

// Using sx prop directly in the SnackBarIcon component

function SnackBarIcon({ variant }: { variant: VariantType }): JSX.Element | null {
  switch (variant) {
    case 'success':
      return (
        <Box
          component={SuccessIcon}
          sx={{
            fill: 'currentColor',
            marginRight: 1,
          }}
        />
      );
    case 'info':
      return (
        <Box
          component={InfoIcon}
          sx={{
            marginRight: 1,
          }}
        />
      );
    default:
      return null;
  }
}

interface BaseSnackbarProps extends Pick<
  CustomContentProps,
  'action' | 'className' | 'id' | 'message' | 'variant'
> {
  children?: React.ReactNode;
  dataCy?: string;
  paperProps?: Partial<PaperProps>;
}

export const BaseSnackbar = forwardRef<HTMLDivElement, BaseSnackbarProps>(
  function BaseSnackbar(props, ref) {
    const {
      action,
      children,
      className,
      dataCy,
      id,
      message,
      paperProps,
      variant = 'default',
    } = props;
    const { closeSnackbar } = useSnackbar();

    function handleClose(): void {
      closeSnackbar(id);
    }

    const defaultAction = (
      <IconButton
        color="primary"
        size="large"
        sx={{ color: theme => theme.appColors.iconsType }}
        onClick={handleClose}
      >
        <CloseIcon
          sx={{
            height: 12,
            width: 12,
          }}
        />
      </IconButton>
    );

    return (
      <Paper
        ref={ref}
        square
        className={className}
        data-cy={dataCy}
        sx={theme => ({
          ...getTypographyForSx(theme.typography.caption),
          alignItems: 'center',
          border: `1px solid ${theme.appColors.rule}`,
          borderRadius: 3,
          boxShadow: '0 2px 4px 0 rgba(50, 74, 82, 0.32)',
          display: 'flex',
          flexGrow: 1,
          flexWrap: 'wrap',
          gap: theme.spacing(1),
          padding: theme.spacing(0.75, 1),
          zIndex: theme.zIndex.snackbar,
          [theme.breakpoints.up('sm')]: {
            flexGrow: 'initial',
            minWidth: 'unset',
          },
          ...getVariantStyles(variant, theme),
        })}
        {...paperProps}
      >
        <SnackBarIcon variant={variant} />
        <Box
          sx={theme => ({
            alignItems: 'baseline',
            display: 'flex',
            maxWidth: 325,
            padding: theme.spacing(1, 0),
          })}
        >
          {children ?? message}
        </Box>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            marginLeft: 'auto',
          }}
        >
          {typeof action === 'function' ? action(id) : (action ?? defaultAction)}
        </Box>
      </Paper>
    );
  }
);
